import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import { CSVLink } from 'react-csv';
import Swal from 'sweetalert2';
import axios from "axios";
import { useUser } from '../context/UserContext';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const NewEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const { selectedCenter } = useUser();
  const [academy, setAcademy] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [academyList, setAcademyList] = useState([]);
  const token = localStorage.getItem('auth-token');
  const [selectedSport, setSelectedSport] = useState('');
  const [sportOptions, setSportOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const abortRef = useRef(null);
  const latestReq = useRef(0);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL_FEEDBACK_FETCH, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const options = Object.entries(data.data.academyNameMap).map(([id, name]) => ({
            value: parseInt(id, 10),
            label: name,
          }));
          setAcademyList(options);

          if (!academy) {
            const def = options.find(opt => opt.value === Number(selectedCenter));
            if (def) setAcademy(def.value);
          }
        }
      })
      .catch(error => {
        console.error("Error fetching academy options:", error);
      });
  }, [token, selectedCenter]);

  const fetchData = useCallback(async () => {

    const reqId = ++latestReq.current;
    setLoading(true);
    setError('');
    setEnrollments([]);

    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await axios.get(
        `${process.env.REACT_APP_API_DROPOFFS}?startDate=${dateRange.start}&endDate=${dateRange.end}&academyIds=${academy || selectedCenter}`,
        {
          headers: {
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        }
      );

      if (reqId !== latestReq.current) return;

      const players = response.data?.data?.inactivePlayers || [];

      const formattedData = players.map((player, index) => ({
        id: index + 1,
        academyName: player.academyName,
        academyId: player.academyId,
        playerName: player.playerName || "Unnamed Player",
        playerContact: player.playerContactNo,
        parentContact: player.parentContactNumber,
        batch: player.batchName,
        plan: player.plan,
        totalNewEnrollments: player.totalNewEnrollments,
        sport: player.sportName || "Unknown",
        weeklyFrequency: player.weekdays
          ? player.weekdays.map(day => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day]).join(", ")
          : "N/A",
        createdAt: player.startDate && !isNaN(new Date(player.startDate))
          ? new Date(player.startDate).toISOString().split("T")[0]
          : "Invalid Date",
        dueIds: player.paymentDueIds ? player.paymentDueIds.join(", ") : "N/A",
      }));

      const sportsSet = new Set();
      players.forEach(p => { if (p?.sportName) sportsSet.add(p.sportName); });
      const sportsOptions = Array.from(sportsSet).map(sport => ({ value: sport, label: sport }));

      setSportOptions(sportsOptions);
      setEnrollments(formattedData);
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      } else {
        setError('Something went wrong while fetching data.');
      }
    } finally {
      if (reqId === latestReq.current) setLoading(false);
    }
  }, [dateRange.start, dateRange.end, selectedCenter, academy]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (academy || selectedCenter) {
        fetchData();
      }
    }, 300);
    return () => clearTimeout(t);
  }, [fetchData, academy, selectedCenter]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const headers = [
    { label: "Academy Name", key: "academyName" },
    { label: "Player Name", key: "playerName" },
    { label: "Player Contact", key: "playerContact" },
    { label: "Parent Contact", key: "parentContact" },
    { label: "Batch", key: "batch" },
    { label: "Plan (M/Q/Y)", key: "plan" },
    { label: "Sport", key: "sport" },
    { label: "Weekly Frequency", key: "weeklyFrequency" },
  ];

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (event) => {
    const value = Number(event.target.value);
    setrowsPerPage(value);
    setCurrentPage(1);
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };
  const sortData = (data) => {
    const { key, direction } = sortConfig || {};
    if (!key) return data;
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      if (typeof aValue === "string") aValue = aValue.trim().toLowerCase();
      if (typeof bValue === "string") bValue = bValue.trim().toLowerCase();
      if (aValue < bValue) return direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const filteredData = enrollments.filter((enrollment) => {
    const isAcademyMatch = academy ? Number(enrollment.academyId) === Number(academy) : true;
    const isSportMatch = selectedSport ? enrollment.sport === selectedSport : true;
    const isSearchMatch = searchTerm
      ? Object.values(enrollment).some((value) =>
        (value ?? "").toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
      : true;
    return isAcademyMatch && isSportMatch && isSearchMatch;
  });

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">{page}</span>
      )
    );

  const totalEntries = filteredData.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const academyOptions = academyList;

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login">
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${academy ? "has-value" : ""}`}
            classNamePrefix="react-select"
            options={academyOptions}
            value={academyOptions.find(option => option.value === academy)}
            onChange={(selectedOption) => setAcademy(selectedOption?.value || '')}
            placeholder="Select Academy"
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {academy && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedSport ? "has-value" : ""}`}
            classNamePrefix="react-select"
            options={sportOptions}
            value={sportOptions.find(opt => opt.value === selectedSport) || null}
            onChange={(opt) => setSelectedSport(opt ? opt.value : '')}
            placeholder="Select Sport"
            menuPortalTarget={document.body}
            isClearable
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {selectedSport && <label htmlFor="sport">Sport</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${dateRange.start ? "has-value" : ""}`}
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {dateRange.start && <label htmlFor="startDate">Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${dateRange.end ? "has-value" : ""}`}
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {dateRange.end && <label htmlFor="endDate">End Date</label>}
        </div>
      </div>

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>

        <CSVLink
          data={filteredData}
          headers={headers}
          filename="Dropouts.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={fetchData} disabled={loading}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span>
            <span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {!loading && !error && filteredData.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      {!loading && !error && filteredData.length > 0 && (
        <>
          <div className="tt-table-container">
            <table className="tt-table">
              <thead>
                <tr>
                  {headers.map(({ key, label }) => (
                    <th
                      key={key}
                      className="challenges-column"
                      onClick={() => handleSort(key)}
                    >
                      <div className="th-content">
                        <span>{label}</span>
                        <FiChevronDown
                          className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortData(currentRows).map((enrollment) => (
                  <tr key={enrollment.id} className="border">
                    <td>{enrollment.academyName}</td>
                    <td>{enrollment.playerName}</td>
                    <td>{enrollment.playerContact}</td>
                    <td>{enrollment.parentContact}</td>
                    <td>{enrollment.batch}</td>
                    <td>{enrollment.plan}</td>
                    <td>{enrollment.sport}</td>
                    <td>{enrollment.weeklyFrequency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="showing-entries">
              Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
            </div>
            <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>
              Previous
            </button>
            {renderPagination()}
            <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewEnrollments;
